import React, { useState } from 'react';
import { delta, formatScore, ratingBy, revealState } from './scoring';
import { formatStubDate } from './dates';
import ScoreLine from './ScoreLine';
import RatingPanel from './RatingPanel';
import MediaStrip from './MediaStrip';

const Note = ({ who, note }) =>
  note ? (
    <p className="helen-note">
      <span className="helen-note-who">{who}</span>
      {note}
    </p>
  ) : null;

const TicketStub = ({ entry, category, me, them, viewerId, mediaUrls, onSaveRating, onUploadMedia, onDeleteMedia }) => {
  const [editing, setEditing] = useState(false);
  const state = revealState(entry, viewerId);
  const mine = ratingBy(entry.ratings, viewerId);
  const theirs = (entry.ratings || []).find((r) => r.user_id !== viewerId) || null;
  const rating = !mine || editing;

  const save = async (values) => {
    await onSaveRating(entry.id, values);
    setEditing(false);
  };

  return (
    <article className="helen-stub" data-testid={`stub-${entry.id}`} data-state={state}>
      <div className="helen-stub-counterfoil">
        <span className="helen-stub-emoji" aria-hidden="true">
          {category?.emoji}
        </span>
        <span className="helen-stub-category">{category?.label || entry.category}</span>
        <span className="helen-stub-date">{formatStubDate(entry.occurred_on)}</span>
      </div>

      <div className="helen-stub-body">
        <h2 className="helen-stub-title">{entry.title}</h2>
        {entry.subtitle && <p className="helen-stub-where">{entry.subtitle}</p>}

        {state === 'revealed' && mine && theirs && (
          <ScoreLine
            mine={mine}
            theirs={theirs}
            me={me}
            them={them}
            gap={delta(entry.ratings)}
          />
        )}

        {state === 'waiting' && (
          <div className="helen-half">
            <span className="helen-half-score">{formatScore(mine?.score)}</span>
            <p className="helen-half-caption">
              Yours is in. Waiting on {them?.display_name || 'her'}.
            </p>
          </div>
        )}

        {state === 'hidden' && (
          <div className="helen-hidden" data-hidden-score>
            <span className="helen-hatch" aria-hidden="true" />
            <p className="helen-hidden-caption">
              {them?.display_name || 'She'} has rated this. Add yours to see it.
            </p>
          </div>
        )}

        {state === 'unrated' && (
          <p className="helen-unrated">Neither of you has put a number on this yet.</p>
        )}

        {(state === 'revealed' || state === 'waiting') && (
          <Note who={me?.initial} note={mine?.note} />
        )}
        {state === 'revealed' && <Note who={them?.initial} note={theirs?.note} />}

        {rating ? (
          <RatingPanel
            entry={entry}
            mine={mine}
            onSave={save}
            onCancel={editing ? () => setEditing(false) : null}
          />
        ) : (
          <button type="button" className="helen-linkbutton" onClick={() => setEditing(true)}>
            Change yours
          </button>
        )}

        <MediaStrip
          entry={entry}
          mediaUrls={mediaUrls}
          onUpload={(file) => onUploadMedia(entry.id, file)}
          onDelete={onDeleteMedia}
        />
      </div>
    </article>
  );
};

export default TicketStub;
